import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Deal } from '../../types';
import DealCard from './DealCard';
import DealDetails from './DealDetails';
import DealMetrics from './DealMetrics';
import DealFilters from './DealFilters';
import DealAnalytics from './DealAnalytics';
import { useDeals } from '../../store/DealsContext';
import { AlertTriangle } from 'lucide-react';

const STATUSES = [
  { id: 'prospect', label: 'Prospect', color: 'blue' },
  { id: 'initial_contact', label: 'Initial Contact', color: 'purple' },
  { id: 'due_diligence', label: 'Due Diligence', color: 'yellow' },
  { id: 'negotiation', label: 'Negotiation', color: 'orange' },
  { id: 'closing', label: 'Closing', color: 'green' },
  { id: 'completed', label: 'Completed', color: 'gray' }
];

export default function DealPipeline() {
  const { deals, updateDeal } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('value');
  const [showAnalytics, setShowAnalytics] = useState(true);

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deal.targetCompany.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType ? deal.type === filterType : true;
    return matchesSearch && matchesType;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'value':
        return b.value - a.value;
      case 'date':
        return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const deal = deals.find(d => d.id === result.draggableId);
    if (deal) {
      const updatedDeal = { 
        ...deal, 
        status: result.destination.droppableId,
        lastModified: new Date().toISOString()
      };
      updateDeal(updatedDeal);
    }
  };

  const highValueDeals = filteredDeals.filter(d => d.value > 50000000);
  const urgentDeals = filteredDeals.filter(d => {
    const closeDate = new Date(d.expectedCloseDate);
    const today = new Date();
    const daysUntilClose = Math.ceil((closeDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilClose <= 30 && d.status !== 'completed';
  });

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Deal Pipeline</h2>
          <p className="mt-2 text-gray-600">
            {filteredDeals.length} active deals • Total value: {
              new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact'
              }).format(filteredDeals.reduce((sum, deal) => sum + deal.value, 0))
            }
          </p>
        </div>
        
        <button
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
        >
          {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
        </button>
      </div>

      {showAnalytics && <DealAnalytics deals={filteredDeals} />}
      
      <DealMetrics deals={filteredDeals} />

      {(highValueDeals.length > 0 || urgentDeals.length > 0) && (
        <div className="grid grid-cols-2 gap-6">
          {highValueDeals.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">
                High-Value Deals ({highValueDeals.length})
              </h3>
              <div className="space-y-2">
                {highValueDeals.slice(0, 3).map(deal => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-blue-600">{deal.name}</span>
                    <span className="font-medium text-blue-800">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        notation: 'compact'
                      }).format(deal.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {urgentDeals.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <h3 className="text-sm font-medium text-red-800">
                  Urgent Deals ({urgentDeals.length})
                </h3>
              </div>
              <div className="space-y-2">
                {urgentDeals.slice(0, 3).map(deal => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-red-600">{deal.name}</span>
                    <span className="text-red-800">
                      Closes: {new Date(deal.expectedCloseDate).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      <DealFilters
        onSearch={setSearchTerm}
        onFilterChange={setFilterType}
        onSortChange={setSortBy}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-6 gap-4 h-[calc(100vh-12rem)] overflow-hidden">
          {STATUSES.map((status) => (
            <div key={status.id} className="flex flex-col h-full">
              <div className={`bg-${status.color}-50 p-3 rounded-t-lg`}>
                <h3 className="font-medium text-gray-700">{status.label}</h3>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-gray-500">
                    {filteredDeals.filter(d => d.status === status.id).length} deals
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      notation: 'compact'
                    }).format(
                      filteredDeals
                        .filter(d => d.status === status.id)
                        .reduce((sum, deal) => sum + deal.value, 0)
                    )}
                  </span>
                </div>
              </div>
              
              <Droppable droppableId={status.id}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 overflow-y-auto bg-${status.color}-50/20 p-2 rounded-b-lg`}
                  >
                    {filteredDeals
                      .filter(deal => deal.status === status.id)
                      .map((deal, index) => (
                        <Draggable
                          key={deal.id}
                          draggableId={deal.id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedDeal(deal)}
                            >
                              <DealCard deal={deal} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {selectedDeal && (
        <DealDetails
          deal={selectedDeal}
          onClose={() => setSelectedDeal(null)}
          onUpdate={updateDeal}
        />
      )}
    </div>
  );
}